import isArray from 'lodash/isArray'

type Color = string
type ColorMap = Record<string, { background: Color, border?: string, orientation?: string, text?: string }>

const primaryColors: ColorMap = {
  yellow: {
    background: '#fd3',
    text: '#000',
  },
  red: { background: '#ff0003' },
  orange: { background: '#fb863b' },
  green: { background: '#4b8e6b' },
  blue: { background: '#0f73ad' },
  beige: {
    background: '#ffc',
    border: '#d2d2d2',
    text: '#000',
  },
  black: {
    background: '#1C1915',
    text: '#FAF6F0',
  },
  gray: { background: '#929292' },
  brown: { background: '#6f3e18' },
  purple: { background: '#9349aa' },
  pink: { background: '#f563b9' },
  white: {
    background: '#FAF6F0',
    border: '#1C1915',
  },
}

const colors: ColorMap = {
  'beiges__light-beige': { background: '#f5e7a1' },
  'beiges__light-stone': { background: '#d4b97d' },
  'blues__arctic-blue': { background: '#78adc2' },
  'blues__azure-blue': { background: '#264d7e' },
  'blues__baby-blue': { background: '#89CFF0' },
  'blues__berry-blue': { background: '#2c3e75' },
  'blues__cobalt-blue': { background: '#0047ab' },
  'blues__deep-blue': { background: '#3f687d' },
  'blues__light-blue': { background: '#4f81c5' },
  'blues__navy-blue': { background: '#173679' },
  'blues__neon-blue': { background: '#4d4dff' },
  'blues__petrol-blue': { background: '#216477' },
  'blues__royal-blue': { background: '#002366' },
  'blues__sky-blue': { background: '#87ceeb' },
  'blues__turquoise-blue': { background: '#00ffef' },
  'browns__caramel-brown': { background: '#bd8f56' },
  'browns__chocolate-brown': { background: '#612D08' },
  'browns__dark-brown': { background: '#5c422e' },
  'browns__dark-earth': { background: '#7b6b4f' },
  'browns__golden-brown': { background: '#a86c29' },
  'browns__light-brown': { background: '#9e7339' },
  'browns__true-brown': { background: '#74542f' },
  'camouflage-gray': { background: '#a0a9aa' },
  'golds__gold-tone': { background: '#fcc808' },
  'golds__rose-gold': { background: '#e7b2a4' },
  'grays__dark-gray': { background: '#6b7477' },
  'grays__light-gray': { background: '#bec0b8' },
  'grays__metallic-gray': { background: '#8e8e8e' },
  'grays__off-white': { background: '#f5f5f5' },
  'grays__smoke-gray': { background: '#7b93a3' },
  'greens__army-green': { background: '#7e8f6e' },
  'greens__bold-green': { background: '#44945e' },
  'greens__bright-green': { background: '#507d3a' },
  'greens__bronze-green': { background: '#6a7031' },
  'greens__emerald-green': { background: '#428b64' },
  'greens__forest-green': { background: '#506b52' },
  'greens__khaki-green': { background: '#7e805d' },
  'greens__light-green': { background: '#bcd890' },
  'greens__lime-green': { background: '#9fc131' },
  'greens__mint-green': { background: '#aaf0d1' },
  'greens__neon-green': { background: '#39ff14' },
  'greens__olive-green': { background: '#4b5729' },
  'greens__sea-green': { background: '#96bf65' },
  'oranges__burnt-orange': { background: '#CC5500' },
  'oranges__coral-orange': { background: '#FF7F50' },
  'oranges__light-orange': { background: '#FFD580' },
  'oranges__neon-orange': { background: '#FF5F1F' },
  'oranges__true-orange': { background: '#ef841e' },
  'oranges__warm-orange': { background: '#f24816' },
  'pinks__baby-pink': { background: '#f4c2c2' },
  'pinks__coral-pink': { background: '#F88379' },
  'pinks__hot-pink': { background: '#ff69b4' },
  'pinks__neon-pink': { background: '#fe019a' },
  'pinks__peach-blossom': { background: '#f1c3cd' },
  'pinks__rose-pink': { background: '#fbded6' },
  'pinks__salmon-pink': { background: '#f3b28b' },
  'purples__dark-crimson': { background: '#613339' },
  'purples__dark-violet': { background: '#6e4a75' },
  'purples__deep-purple': { background: '#570861' },
  'purples__light-violet': { background: '#c9a8ce' },
  'reds__cherry-red': { background: '#c41c22' },
  'reds__currant-red': { background: '#d21e2b' },
  'reds__deep-red': { background: '#850101' },
  'reds__gulf-red': { background: '#793932' },
  'reds__soft-red': { background: '#F47174' },
  'reds__true-red': { background: '#dd3420' },
  'yellows__bold-yellow': { background: '#fde706' },
  'yellows__canary-yellow': { background: '#feec04' },
  'yellows__deep-cream': { background: '#fdf07a' },
  'yellows__lemon-yellow': { background: '#fef96a' },
  'yellows__mustard-yellow': { background: '#dbac50' },
  'yellows__neon-yellow': { background: '#faed27' },
  'yellows__ocher-yellow': { background: '#cb9d06' },
  'yellows__pale-yellow': { background: '#fced96' },
  'yellows__soft-yellow': { background: '#fefebe' },
  beiges__champagne: { background: '#faf2c5' },
  beiges__ivory: { background: '#FFFEF2' },
  beiges__sand: { background: '#a89177' },
  beiges__tan: { background: '#d2b48c' },
  browns__cognac: { background: '#A54A21' },
  browns__rust: { background: '#83422b' },
  browns__taupe: { background: '#483c32' },
  browns__terracotta: { background: '#a65341' },
  gold: { background: '#EBC780' },
  grays__charcoal: { background: '#2d2b2f' },
  grays__graphite: { background: '#4f555b' },
  grays__gunmetal: { background: '#2a3439' },
  grays__lead: { background: '#525b55' },
  grays__slate: { background: '#6f7264' },
  oranges__apricot: { background: '#fb9c06' },
  purples__crimson: { background: '#8b1a32' },
  purples__lavender: { background: '#9873AC' },
  purples__maroon: { background: '#471b21' },
  purples__ruby: { background: '#982d57' },
  reds__bordeaux: { background: '#611e26' },
  reds__burgundy: { background: '#800020' },
  reds__wine: { background: '#58181F' },
  steel: { background: '#D3D2D2' },
  silver: { background: '#E8E8E8' },
  steels__steel: { background: '#D3D2D2' },
}

const isOfGradientArray = (object: any): object is Array<string> => isArray(object)

export const getColorVariables = (key: string): { background?: string, border?: string } => {
  const color = colors[key]
  if (color) {
    return {
      background: color.background,
      border: color.border,
    }
  }
  return {
    background: primaryColors[key]?.background ?? key,
    border: primaryColors[key]?.border ?? primaryColors[key]?.background ?? key,
  }
}

// getColorMapping prioritizes the override color, then the colors object, and finally the primaryColors object
// the colors are used as css variable overrides, and as such returns undefined if no color is found, to allow the default color to be used
export const getColorMapping = (key: string, override: string | undefined, isBackground = true) => {
  if (override) {
    return override
  }

  let color = colors[key]

  if (!color) {
    color = primaryColors[key]
  }

  if (color) {
    if (isBackground) {
      const background = isOfGradientArray(color.background)
        ? `linear-gradient(${color.orientation ?? '180deg'}, ${color.background.join(',')})`
        : color.background

      return background || undefined
    }

    return color.text || undefined
  }

  return undefined
}
